import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import AdminLayout from './layouts/AdminLayout';
import Dashboard from './pages/Dashboard';
import { FeedbackProvider, LoadingState } from './components';
import { ErrorBoundary } from './components/ErrorBoundary';
import { clearReturnTo, rememberReturnTo, returnTo } from './utils/returnTo';

// Sign-in and the dashboard come with the app; every other page is downloaded when it's first
// opened, so the first load stays small. The pages people open most are fetched in the
// background once the app is idle, so they still open instantly.
const pages = {
  Register: () => import('./pages/Register'),
  Join: () => import('./pages/Join'),
  ForgotPassword: () => import('./pages/ForgotPassword'),
  ResetPassword: () => import('./pages/ResetPassword'),
  ConnectShopify: () => import('./pages/ConnectShopify'),
  Credits: () => import('./pages/Credits'),
  PackingSlip: () => import('./pages/PackingSlip'),
  Settings: () => import('./pages/Settings'),
  StorageUnits: () => import('./pages/StorageUnits'),
  StorageUnitEditor: () => import('./pages/StorageUnitEditor'),
  Orders: () => import('./pages/Orders'),
  Picking: () => import('./pages/Picking'),
  Users: () => import('./pages/Users'),
  SkuManager: () => import('./pages/SkuManager'),
  ActivityLog: () => import('./pages/ActivityLog'),
};
const Register = lazy(pages.Register);
const Join = lazy(pages.Join);
const ForgotPassword = lazy(pages.ForgotPassword);
const ResetPassword = lazy(pages.ResetPassword);
const ConnectShopify = lazy(pages.ConnectShopify);
const Credits = lazy(pages.Credits);
const PackingSlip = lazy(pages.PackingSlip);
const Settings = lazy(pages.Settings);
const StorageUnits = lazy(pages.StorageUnits);
const StorageUnitEditor = lazy(pages.StorageUnitEditor);
const Orders = lazy(pages.Orders);
const Picking = lazy(pages.Picking);
const Users = lazy(pages.Users);
const SkuManager = lazy(pages.SkuManager);
const ActivityLog = lazy(pages.ActivityLog);

const PREFETCH = ['Orders', 'Picking', 'StorageUnits', 'SkuManager'];

function usePrefetchPages(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const handle = idle(() => PREFETCH.forEach(name => pages[name]().catch(() => {})));
    return () => cancel(handle);
  }, [enabled]);
}

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) clearReturnTo();
  }, [isAuthenticated]);

  if (loading) {
    return <LoadingState />;
  }

  if (!isAuthenticated) {
    // Come back here after signing in (e.g. a "finish connecting your store" link)
    if (location.pathname !== '/') rememberReturnTo(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Sign-in and sign-up pages send someone already signed in into the app
function SignedOutRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingState />;
  return isAuthenticated ? <Navigate to={returnTo()} replace /> : children;
}

// Admin-only pages send staff back to the dashboard (the API enforces this too)
function AdminRoute({ children }) {
  const { isAdmin } = useAuth();
  return isAdmin ? children : <Navigate to="/" replace />;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  usePrefetchPages(isAuthenticated);
  return (
    <Suspense fallback={<LoadingState />}>
    <Routes>
      <Route path="/login" element={<SignedOutRoute><Login /></SignedOutRoute>} />
      <Route path="/register" element={<SignedOutRoute><Register /></SignedOutRoute>} />
      <Route path="/join/:token" element={<SignedOutRoute><Join /></SignedOutRoute>} />
      <Route path="/forgot-password" element={<SignedOutRoute><ForgotPassword /></SignedOutRoute>} />
      <Route path="/reset-password/:token" element={<SignedOutRoute><ResetPassword /></SignedOutRoute>} />
      <Route path="/connect/shopify" element={<ProtectedRoute><ConnectShopify /></ProtectedRoute>} />

      {/* Picking is a full-screen focus mode, outside the app shell */}
      <Route path="/picking/batch" element={<ProtectedRoute><Picking /></ProtectedRoute>} />
      <Route path="/picking/:id" element={<ProtectedRoute><Picking /></ProtectedRoute>} />
      <Route path="/slip" element={<ProtectedRoute><PackingSlip /></ProtectedRoute>} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="settings" element={<AdminRoute><Settings /></AdminRoute>} />
        <Route path="storage" element={<StorageUnits />} />
        <Route path="storage/:id" element={<StorageUnitEditor />} />
        <Route path="orders" element={<Orders />} />
        <Route path="users" element={<AdminRoute><Users /></AdminRoute>} />
        <Route path="skus" element={<SkuManager />} />
        <Route path="credits" element={<Credits />} />
        <Route path="activity" element={<AdminRoute><ActivityLog /></AdminRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <FeedbackProvider>
            <AppRoutes />
          </FeedbackProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
